import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider } from 'react-router-dom'
import { router } from './router'
import { ErrorBoundary } from './components/ui/ErrorBoundary'
import './index.css'

// Service Worker for PWA
if ('serviceWorker' in navigator) {
  if (import.meta.env.PROD) {
    // 페이지 로드 시점에 이미 SW 컨트롤러가 있었는지 기록.
    // 최초 설치(컨트롤러 없음)에서는 clients.claim()이 controllerchange를 발생시키지만
    // 이는 업데이트가 아니므로 불필요한 새로고침을 막는다. 업데이트 시에만 새로고침.
    const hadController = !!navigator.serviceWorker.controller
    // Reload only when the new SW has taken control (safe timing)
    let refreshing = false
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return
      if (!hadController) return // 최초 설치 → 강제 새로고침 방지
      refreshing = true
      window.location.reload()
    })

    window.addEventListener('load', async () => {
      try {
        const registration = await navigator.serviceWorker.register('/sw.js')
        const { usePwaUpdateStore } = await import('./stores/pwaUpdateStore')

        // Handle waiting worker — 배너 표시 후 사용자가 업데이트 선택
        if (registration.waiting && navigator.serviceWorker.controller) {
          usePwaUpdateStore.getState().showUpdate(registration.waiting)
        }

        // Detect new updates — 배너로 알림
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (!newWorker) return

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              usePwaUpdateStore.getState().showUpdate(newWorker)
            }
          })
        })

        // Check for updates immediately + every 5 minutes
        registration.update()
        setInterval(() => registration.update(), 5 * 60 * 1000)

        // Tab focus → 즉시 SW 업데이트 체크 (스로틀 제거 — 실시간 채팅 업데이트 우선)
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            registration.update()
          }
        })
      } catch (error) {
        console.error('SW registration failed:', error)
      }
    })
  } else {
    // Development: Unregister SW to prevent caching issues
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => registration.unregister())
    })
  }
}

// PWA Install Prompt handling
let deferredPrompt: BeforeInstallPromptEvent | null = null

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  deferredPrompt = e as BeforeInstallPromptEvent
  window.dispatchEvent(new CustomEvent('pwaInstallAvailable'))
})

  // Export install function
  ; (window as Window & { installPWA?: () => Promise<boolean> }).installPWA = async () => {
    if (!deferredPrompt) return false
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    deferredPrompt = null
    return outcome === 'accepted'
  }

// Global error handlers — structured logging to localStorage
function logGlobalError(type: string, message: string, stack?: string) {
  try {
    const entry = { timestamp: new Date().toISOString(), type, message, stack: stack?.slice(0, 500) }
    const key = 'tdl_error_log'
    const existing = JSON.parse(localStorage.getItem(key) || '[]')
    existing.push(entry)
    if (existing.length > 20) existing.splice(0, existing.length - 20)
    localStorage.setItem(key, JSON.stringify(existing))
  } catch { /* ignore */ }
}

window.onerror = (_msg, _src, _line, _col, error) => {
  logGlobalError('uncaught', error?.message || String(_msg), error?.stack)
}

window.addEventListener('unhandledrejection', (event) => {
  const reason = event.reason
  logGlobalError(
    'unhandled-rejection',
    reason instanceof Error ? reason.message : String(reason),
    reason instanceof Error ? reason.stack : undefined,
  )
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  </StrictMode>
)
